/** WebGL2 plumbing: context creation, shader preprocessing, programs with typed uniforms, textures, FBOs. */

import type { Mat4 } from './mat4';

export interface GLCaps {
  colorBufferFloat: boolean;
  floatLinear: boolean;
  max3DTextureSize: number;
  maxTextureSize: number;
  maxDrawBuffers: number;
  renderer: string;
}

export interface GLContext {
  gl: WebGL2RenderingContext;
  canvas: HTMLCanvasElement;
  caps: GLCaps;
}

export class ShaderError extends Error {
  constructor(
    message: string,
    readonly log: string,
    readonly source: string,
  ) {
    super(message);
    this.name = 'ShaderError';
  }
}

export function createGLContext(canvas: HTMLCanvasElement): GLContext {
  const gl = canvas.getContext('webgl2', {
    alpha: false,
    antialias: false,
    depth: false,
    stencil: false,
    premultipliedAlpha: false,
    preserveDrawingBuffer: false,
    powerPreference: 'high-performance',
    desynchronized: false,
  });

  if (!gl) {
    throw new Error('WebGL2 is not available in this browser. The time cube needs WebGL2 (3D textures).');
  }

  const colorBufferFloat = !!gl.getExtension('EXT_color_buffer_float');
  const floatLinear = !!gl.getExtension('OES_texture_float_linear');
  const dbgInfo = gl.getExtension('WEBGL_debug_renderer_info');
  const renderer = dbgInfo
    ? String(gl.getParameter(dbgInfo.UNMASKED_RENDERER_WEBGL))
    : String(gl.getParameter(gl.RENDERER));

  return {
    gl,
    canvas,
    caps: {
      colorBufferFloat,
      floatLinear,
      max3DTextureSize: gl.getParameter(gl.MAX_3D_TEXTURE_SIZE) as number,
      maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE) as number,
      maxDrawBuffers: gl.getParameter(gl.MAX_DRAW_BUFFERS) as number,
      renderer,
    },
  };
}

/** Resolves `#include "name.glsl"` directives against a chunk map, recursively. */
export function resolveIncludes(source: string, chunks: Record<string, string>, seen: string[] = []): string {
  return source.replace(/^[ \t]*#include[ \t]+"([^"]+)"[ \t]*$/gm, (_m, name: string) => {
    if (seen.includes(name)) return `// circular include skipped: ${name}`;
    const chunk = chunks[name];
    if (chunk === undefined) {
      throw new Error(`Unknown shader include: "${name}"`);
    }
    return resolveIncludes(chunk, chunks, [...seen, name]);
  });
}

/** Injects `#define` lines directly after the `#version` directive. */
export function withDefines(source: string, defines: Record<string, string | number | boolean>): string {
  const lines = Object.entries(defines)
    .filter(([, v]) => v !== false && v !== undefined && v !== null)
    .map(([k, v]) => (v === true ? `#define ${k}` : `#define ${k} ${v}`));
  if (!lines.length) return source;
  const match = source.match(/^#version[^\n]*\n/);
  if (!match) return `${lines.join('\n')}\n${source}`;
  return source.slice(0, match[0].length) + lines.join('\n') + '\n' + source.slice(match[0].length);
}

function annotate(source: string, log: string): string {
  const lineNumbers = new Set<number>();
  for (const m of log.matchAll(/^\s*(?:ERROR|WARNING)?:?\s*\d+:(\d+)/gm)) {
    lineNumbers.add(Number(m[1]));
  }
  const lines = source.split('\n');
  const out: string[] = [];
  for (const n of [...lineNumbers].sort((a, b) => a - b)) {
    for (let i = Math.max(1, n - 2); i <= Math.min(lines.length, n + 2); i++) {
      out.push(`${i === n ? '>' : ' '} ${String(i).padStart(4)} | ${lines[i - 1]}`);
    }
    out.push('');
  }
  return out.join('\n');
}

function compileShader(gl: WebGL2RenderingContext, type: number, source: string, label: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('createShader failed');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader) ?? 'unknown error';
    gl.deleteShader(shader);
    throw new ShaderError(`${label} failed to compile:\n${log}\n${annotate(source, log)}`, log, source);
  }
  return shader;
}

interface UniformInfo {
  location: WebGLUniformLocation;
  type: number;
  size: number;
}

/** A linked program with introspected uniforms and a single `set()` entry point. */
export class Program {
  readonly handle: WebGLProgram;
  private readonly uniforms = new Map<string, UniformInfo>();

  constructor(
    private readonly gl: WebGL2RenderingContext,
    vertexSource: string,
    fragmentSource: string,
    readonly label = 'program',
  ) {
    const vs = compileShader(gl, gl.VERTEX_SHADER, vertexSource, `${label} (vertex)`);
    let fs: WebGLShader;
    try {
      fs = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource, `${label} (fragment)`);
    } catch (err) {
      gl.deleteShader(vs);
      throw err;
    }

    const program = gl.createProgram();
    if (!program) {
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      throw new Error('createProgram failed');
    }
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    gl.deleteShader(vs);
    gl.deleteShader(fs);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const log = gl.getProgramInfoLog(program) ?? 'unknown link error';
      gl.deleteProgram(program);
      throw new ShaderError(`${label} failed to link:\n${log}`, log, fragmentSource);
    }

    this.handle = program;

    const count = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS) as number;
    for (let i = 0; i < count; i++) {
      const info = gl.getActiveUniform(program, i);
      if (!info) continue;
      const name = info.name.replace(/\[0\]$/, '');
      const location = gl.getUniformLocation(program, info.name);
      if (!location) continue;
      this.uniforms.set(name, { location, type: info.type, size: info.size });
    }
  }

  use(): this {
    this.gl.useProgram(this.handle);
    return this;
  }

  has(name: string): boolean {
    return this.uniforms.has(name);
  }

  /**
   * Sets a uniform, dispatching on its declared GLSL type. Names the compiler
   * optimised away are silently ignored, which keeps callers free to set uniforms
   * a given shader variant does not happen to use.
   */
  set(name: string, value: number | boolean | ArrayLike<number> | Mat4): this {
    const info = this.uniforms.get(name);
    if (!info) return this;
    const { gl } = this;
    const loc = info.location;

    switch (info.type) {
      case gl.FLOAT:
        gl.uniform1f(loc, value as number);
        break;
      case gl.FLOAT_VEC2:
        gl.uniform2fv(loc, value as Float32Array);
        break;
      case gl.FLOAT_VEC3:
        gl.uniform3fv(loc, value as Float32Array);
        break;
      case gl.FLOAT_VEC4:
        gl.uniform4fv(loc, value as Float32Array);
        break;
      case gl.INT:
      case gl.BOOL:
      case gl.SAMPLER_2D:
      case gl.SAMPLER_3D:
      case gl.SAMPLER_2D_ARRAY:
      case gl.INT_SAMPLER_2D:
      case gl.INT_SAMPLER_3D:
        gl.uniform1i(loc, typeof value === 'boolean' ? (value ? 1 : 0) : (value as number) | 0);
        break;
      case gl.INT_VEC2:
      case gl.BOOL_VEC2:
        gl.uniform2iv(loc, value as Int32Array);
        break;
      case gl.INT_VEC3:
      case gl.BOOL_VEC3:
        gl.uniform3iv(loc, value as Int32Array);
        break;
      case gl.INT_VEC4:
      case gl.BOOL_VEC4:
        gl.uniform4iv(loc, value as Int32Array);
        break;
      case gl.FLOAT_MAT3:
        gl.uniformMatrix3fv(loc, false, value as Float32Array);
        break;
      case gl.FLOAT_MAT4:
        gl.uniformMatrix4fv(loc, false, value as Float32Array);
        break;
      default:
        throw new Error(`${this.label}: unsupported uniform type 0x${info.type.toString(16)} for "${name}"`);
    }
    return this;
  }

  /** Binds a texture to a unit and points the sampler uniform at it. */
  texture(name: string, unit: number, target: number, texture: WebGLTexture | null): this {
    const { gl } = this;
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(target, texture);
    this.set(name, unit);
    return this;
  }

  dispose(): void {
    this.gl.deleteProgram(this.handle);
    this.uniforms.clear();
  }
}

export interface Texture2DOptions {
  width: number;
  height: number;
  internalFormat?: number;
  filter?: number;
  wrap?: number;
}

export function createTexture2D(gl: WebGL2RenderingContext, opts: Texture2DOptions): WebGLTexture {
  const { width, height } = opts;
  const internalFormat = opts.internalFormat ?? gl.RGBA8;
  const filter = opts.filter ?? gl.LINEAR;
  const wrap = opts.wrap ?? gl.CLAMP_TO_EDGE;

  const tex = gl.createTexture();
  if (!tex) throw new Error('createTexture failed');
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texStorage2D(gl.TEXTURE_2D, 1, internalFormat, width, height);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, wrap);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, wrap);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return tex;
}

export interface Texture3DOptions {
  width: number;
  height: number;
  depth: number;
  internalFormat?: number;
  filter?: number;
}

export function createTexture3D(gl: WebGL2RenderingContext, opts: Texture3DOptions): WebGLTexture {
  const { width, height, depth } = opts;
  const internalFormat = opts.internalFormat ?? gl.RGBA8;
  const filter = opts.filter ?? gl.LINEAR;

  const tex = gl.createTexture();
  if (!tex) throw new Error('createTexture failed');
  gl.bindTexture(gl.TEXTURE_3D, tex);
  gl.texStorage3D(gl.TEXTURE_3D, 1, internalFormat, width, height, depth);
  gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);
  gl.bindTexture(gl.TEXTURE_3D, null);
  return tex;
}

/** Simple single-attachment 2D render target. */
export class RenderTarget {
  readonly framebuffer: WebGLFramebuffer;
  readonly texture: WebGLTexture;

  constructor(
    private readonly gl: WebGL2RenderingContext,
    readonly width: number,
    readonly height: number,
    internalFormat: number,
    filter?: number,
  ) {
    this.texture = createTexture2D(gl, { width, height, internalFormat, filter });
    const fbo = gl.createFramebuffer();
    if (!fbo) throw new Error('createFramebuffer failed');
    this.framebuffer = fbo;
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.texture, 0);
    const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    if (status !== gl.FRAMEBUFFER_COMPLETE) {
      throw new Error(`Incomplete framebuffer (0x${status.toString(16)}) for ${width}x${height} target`);
    }
  }

  bind(): void {
    const { gl } = this;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffer);
    gl.viewport(0, 0, this.width, this.height);
  }

  clear(r = 0, g = 0, b = 0, a = 0): void {
    const { gl } = this;
    this.bind();
    gl.clearColor(r, g, b, a);
    gl.clear(gl.COLOR_BUFFER_BIT);
  }

  dispose(): void {
    this.gl.deleteFramebuffer(this.framebuffer);
    this.gl.deleteTexture(this.texture);
  }
}

/** Draws a full-screen quad. Requires a shader that builds position from gl_VertexID. */
export class ScreenQuad {
  private readonly vao: WebGLVertexArrayObject;

  constructor(private readonly gl: WebGL2RenderingContext) {
    const vao = gl.createVertexArray();
    if (!vao) throw new Error('createVertexArray failed');
    this.vao = vao;
  }

  draw(): void {
    const { gl } = this;
    gl.bindVertexArray(this.vao);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    gl.bindVertexArray(null);
  }

  dispose(): void {
    this.gl.deleteVertexArray(this.vao);
  }
}

export function checkGLError(gl: WebGL2RenderingContext, where: string): void {
  const err = gl.getError();
  if (err !== gl.NO_ERROR) {
    const names: Record<number, string> = {
      [gl.INVALID_ENUM]: 'INVALID_ENUM',
      [gl.INVALID_VALUE]: 'INVALID_VALUE',
      [gl.INVALID_OPERATION]: 'INVALID_OPERATION',
      [gl.INVALID_FRAMEBUFFER_OPERATION]: 'INVALID_FRAMEBUFFER_OPERATION',
      [gl.OUT_OF_MEMORY]: 'OUT_OF_MEMORY',
      [gl.CONTEXT_LOST_WEBGL]: 'CONTEXT_LOST_WEBGL',
    };
    throw new Error(`GL error at ${where}: ${names[err] ?? err}`);
  }
}
